import type { TimePickerProps } from 'antd';
import { TimePicker } from 'antd';
import { forwardRef, useEffect, useRef, type CompositionEvent, type ElementRef, type FormEvent } from 'react';
import dayjs, { type Dayjs } from '@shared/lib/dayjs';

export type AppTimePickerProps = TimePickerProps;

type AppTimePickerRef = ElementRef<typeof TimePicker>;
type TimePickerBlurEvent = Parameters<NonNullable<TimePickerProps['onBlur']>>[0];
type TimePickerBlurInfo = Parameters<NonNullable<TimePickerProps['onBlur']>>[1];

const parseTimeInput = (timeStr: string): Dayjs | null => {
  // 24h format: H:mm or HH:mm (hour must be < 24)
  const match24h = timeStr.match(/^(\d{1,2}):(\d{2})$/);
  if (match24h) {
    const [, hourStr, minuteStr] = match24h;
    const hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);
    if (hour >= 0 && hour < 24 && minute >= 0 && minute < 60) {
      return dayjs().hour(hour).minute(minute).second(0).millisecond(0);
    }
    return null;
  }

  // Backward compatibility: Japanese AM/PM input
  const matchJapanese = timeStr.match(/^(\d{1,2}):(\d{2})\s*(午前|午後)$/);
  if (!matchJapanese) return null;

  const [, hourStr, minuteStr, period] = matchJapanese;
  const hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);

  if (hour < 1 || hour > 12 || minute < 0 || minute >= 60) return null;

  let finalHour = hour;
  if (period === '午後' && hour !== 12) finalHour += 12;
  if (period === '午前' && hour === 12) finalHour = 0;

  return dayjs().hour(finalHour).minute(minute).second(0).millisecond(0);
};

const normalizeMinuteValue = (timeStr: string): string => {
  const match = timeStr.match(/^(\d{1,2}):(\d{1,})(\s*(午前|午後))?$/);
  if (!match) return timeStr;

  const [, hourPart, rawMinute, suffix = ''] = match;
  const minute = rawMinute.slice(0, 2);

  if (minute.length === 1) {
    return `${hourPart}:0${minute}${suffix}`;
  }

  if (minute.length === 0) {
    return `${hourPart}:${minute}${suffix}`;
  }

  const clampedMinute = Math.min(parseInt(minute, 10), 59);
  return `${hourPart}:${String(clampedMinute).padStart(2, '0')}${suffix}`;
};

const normalizeHourValue = (timeStr: string): string => {
  const match = timeStr.match(/^(\d{1,2}):(\d{2})(\s*(午前|午後))?$/);
  if (!match) return timeStr;

  const [, rawHour, minute, suffix = ''] = match;
  // In 24h mode, clamp hour to 23 when input exceeds 23.
  if (!suffix.trim()) {
    const hour = parseInt(rawHour, 10);
    const clampedHour = Math.min(Math.max(hour, 0), 23);
    return `${String(clampedHour).padStart(2, '0')}:${minute}`;
  }

  return `${rawHour}:${minute}${suffix}`;
};

const AppTimePicker = forwardRef<AppTimePickerRef, AppTimePickerProps>((props, ref) => {
  const { value, onChange, onBlur, ...rest } = props;
  const isComposing = useRef(false);
  const justCleared = useRef(false);
  const typedCountAfterClear = useRef(0);
  const singleDigitPadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSingleDigitPadTimer = () => {
    if (singleDigitPadTimerRef.current) {
      clearTimeout(singleDigitPadTimerRef.current);
      singleDigitPadTimerRef.current = null;
    }
  };

  const scheduleSingleDigitPad = (input: HTMLInputElement) => {
    clearSingleDigitPadTimer();
    singleDigitPadTimerRef.current = setTimeout(() => {
      if (isComposing.current) return;
      // If user stopped at a single-digit hour, auto-pad to 2 digits.
      if (/^\d$/.test(input.value)) {
        input.value = `0${input.value}:`;
        input.setSelectionRange(3, 3);
      }
      singleDigitPadTimerRef.current = null;
    }, 400);
  };

  useEffect(() => {
    return () => {
      clearSingleDigitPadTimer();
    };
  }, []);

  const getDayjsValue = (val: TimePickerProps['value']): Dayjs | null => {
    if (!val) return null;
    if (dayjs.isDayjs(val)) return val.second(0).millisecond(0);
    const d = dayjs(val, 'HH:mm').second(0).millisecond(0);
    return d.isValid() ? d : null;
  };

  const internalValue = getDayjsValue(value);

  const handleBlur: NonNullable<TimePickerProps['onBlur']> = (e, info) => {
    clearSingleDigitPadTimer();
    const target = e.target;
    if (!(target instanceof HTMLInputElement)) {
      onBlur?.(e as TimePickerBlurEvent, info as TimePickerBlurInfo);
      return;
    }

    const input = target;
    const timeValue = input.value;
    if (!timeValue) {
      // Keep cleared state after blur instead of restoring previous controlled value.
      onChange?.(null, '');
      onBlur?.(e as TimePickerBlurEvent, info as TimePickerBlurInfo);
      return;
    }

    // Auto-fix single digit hour if colon is present (e.g., "9:00" -> "09:00")
    let normalizedValue = timeValue;
    const singleDigitMatch = timeValue.match(/^(\d):(\d{2})(\s*(午前|午後))?$/);
    if (singleDigitMatch) {
      normalizedValue = `0${singleDigitMatch[1]}:${singleDigitMatch[2]}${singleDigitMatch[3] ?? ''}`;
    }

    const normalizedFinalValue = normalizeHourValue(normalizeMinuteValue(normalizedValue));
    if (normalizedFinalValue !== normalizedValue) {
      input.value = normalizedFinalValue;
    }

    const parsed = parseTimeInput(normalizedFinalValue);
    if (parsed && onChange) {
      onChange(parsed, parsed.format('HH:mm'));
    }
    onBlur?.(e as TimePickerBlurEvent, info as TimePickerBlurInfo);
  };

  const handleKeyDown: NonNullable<TimePickerProps['onKeyDown']> = e => {
    const input = e.currentTarget as HTMLInputElement;
    const key = e.key;
    const applyAutoFormattedValue = (nextValue: string, nextCursor: number) => {
      input.value = nextValue;
      input.setSelectionRange(nextCursor, nextCursor);
    };

    if (
      ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Escape'].includes(key)
    ) {
      return;
    }

    if (isComposing.current) return;

    const isDigit = /^\d$/.test(key);
    const isColon = key === ':';
    const isJapaneseMeridiem = ['午', '前', '後'].includes(key);

    if (justCleared.current) {
      if (isDigit) {
        typedCountAfterClear.current += 1;
        return;
      }
      if (isColon) {
        e.preventDefault();
        applyAutoFormattedValue('0:', 2);
      }
      justCleared.current = false;
      typedCountAfterClear.current = 0;
      return;
    }

    if (!isDigit && !isColon && !isJapaneseMeridiem && !/^[a-zA-Z]$/.test(key)) {
      e.preventDefault();
      return;
    }

    if (isDigit) {
      const selectionStart = input.selectionStart || 0;
      const selectionEnd = input.selectionEnd || selectionStart;
      const hasSelection = selectionStart !== selectionEnd;
      const val = input.value;
      const colonIndex = val.indexOf(':');
      const minuteStart = colonIndex >= 0 ? colonIndex + 1 : -1;
      const minutePart =
        minuteStart >= 0 ? (val.slice(minuteStart).match(/^\d{0,2}/)?.[0] ?? '') : '';

      if (
        minuteStart >= 0 &&
        selectionStart >= minuteStart &&
        selectionStart <= minuteStart + 2 &&
        selectionStart === selectionEnd &&
        minutePart.length >= 2
      ) {
        e.preventDefault();
        return;
      }

      if (hasSelection) return;

      if (selectionStart === 1 && val.length === 1 && !val.includes(':') && /^\d$/.test(val[0])) {
        const hour = parseInt(val[0] + key, 10);
        if (hour <= 23) {
          requestAnimationFrame(() => {
            const currentValue = input.value;
            if (/^\d{2}$/.test(currentValue)) {
              applyAutoFormattedValue(`${currentValue}:`, 3);
            }
          });
          return;
        } else {
          e.preventDefault();
          applyAutoFormattedValue('23:', 3);
          return;
        }
      }

      if (selectionStart === 2 && val.length === 2 && !val.includes(':')) {
        e.preventDefault();
        applyAutoFormattedValue(`${val.slice(0, 2)}:${key}`, 4);
        return;
      }
    }

    if (isColon) {
      const selectionStart = input.selectionStart || 0;
      const selectionEnd = input.selectionEnd || selectionStart;
      const val = input.value;
      if (
        selectionStart === 1 &&
        selectionStart === selectionEnd &&
        val.length === 1 &&
        !val.includes(':') &&
        /^\d$/.test(val[0])
      ) {
        e.preventDefault();
        applyAutoFormattedValue(`0${val[0]}:`, 3);
      }
    }
  };

  const handleCompositionStart = () => {
    isComposing.current = true;
  };

  const handleCompositionEnd = (_e: CompositionEvent<HTMLDivElement>) => {
    isComposing.current = false;
  };

  const handleInput = (e: FormEvent<HTMLDivElement>) => {
    if (!justCleared.current || isComposing.current) return;

    const target = e.target;
    if (!(target instanceof HTMLInputElement)) return;

    const currentValue = target.value;
    if (!/^\d{2}$/.test(currentValue)) return;

    const hour = parseInt(currentValue, 10);
    if (hour >= 0 && hour < 24) {
      target.value = `${currentValue}:`;
      target.setSelectionRange(3, 3);
    } else {
      target.value = '23:';
      target.setSelectionRange(3, 3);
    }

    justCleared.current = false;
    typedCountAfterClear.current = 0;
  };

  const handleChange: NonNullable<TimePickerProps['onChange']> = (nextValue, dateString) => {
    clearSingleDigitPadTimer();
    justCleared.current = !nextValue;
    typedCountAfterClear.current = 0;
    onChange?.(nextValue, dateString);
  };

  const handleInputCapture = (e: FormEvent<HTMLDivElement>) => {
    const target = e.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (isComposing.current) return;

    if (/^\d$/.test(target.value)) {
      scheduleSingleDigitPad(target);
    } else {
      clearSingleDigitPadTimer();
    }
  };

  return (
    <div
      onInputCapture={handleInputCapture}
      onInput={handleInput}
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
    >
      <TimePicker
        ref={ref}
        format="HH:mm"
        value={internalValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        {...rest}
      />
    </div>
  );
});

AppTimePicker.displayName = 'AppTimePicker';

export default AppTimePicker;
